import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  openAiSupportChat,
  subscribeAiSupportChatOpen
} from '@/components/navbar/openAiSupportChat';

/** Same key the appearance drawer already writes for widget side. */
const PLACE_KEY = 'goftino_p';
const PLACE_EVENT = 'ai-support-place';

export type AiSupportPlace = 'left' | 'right' | 'none';

export function readAiSupportPlace(): AiSupportPlace {
  try {
    const value = localStorage.getItem(PLACE_KEY);
    if (value === 'right' || value === 'none' || value === 'left') return value;
  } catch {
    /* private mode */
  }
  return 'left';
}

export function writeAiSupportPlace(value: string) {
  localStorage.setItem(PLACE_KEY, value);
  window.dispatchEvent(new Event(PLACE_EVENT));
}

export function AiSupportIcon({ size = 22 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.089 6.038C16.639 6 16.057 6 15.2 6H14a1 1 0 1 1 0-2h1.241c.805 0 1.47 0 2.01.044.563.046 1.08.145 1.565.392a4 4 0 0 1 1.748 1.748c.247.485.346 1.002.392 1.564C21 8.29 21 8.954 21 9.758v3.483c0 .805 0 1.47-.044 2.01-.046.562-.145 1.08-.392 1.564a4 4 0 0 1-1.748 1.749c-.485.247-1.002.345-1.564.391-.541.045-1.206.045-2.01.044h-.21l-2.273 2.652a1 1 0 0 1-1.518 0l-2.272-2.652h-.504c-.67 0-1.222 0-1.675-.03-.469-.032-.903-.1-1.32-.274a4 4 0 0 1-2.166-2.165c-.173-.418-.241-.852-.273-1.321C3 14.756 3 14.204 3 13.534V13.5a1 1 0 1 1 2 0c0 .712 0 1.197.026 1.573.025.367.07.558.126.692a2 2 0 0 0 1.083 1.082c.133.055.324.101.692.126.376.026.86.026 1.573.026h.929a1 1 0 0 1 .759.35L12 19.463l1.812-2.114a1 1 0 0 1 .76-.35h.628c.857 0 1.439 0 1.889-.037.438-.036.663-.1.819-.18a2 2 0 0 0 .874-.875c.08-.156.145-.38.18-.819.037-.45.038-1.032.038-1.888V9.8c0-.857 0-1.439-.038-1.889-.035-.438-.1-.663-.18-.819a2 2 0 0 0-.874-.874c-.156-.08-.38-.145-.82-.18"
      />
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.5 0a1 1 0 0 1 .949.684l.967 2.9 2.9.967a1 1 0 0 1 0 1.898l-2.9.967-.967 2.9a1 1 0 0 1-1.898 0l-.967-2.9-2.9-.967a1 1 0 0 1 0-1.898l2.9-.967.967-2.9A1 1 0 0 1 5.5 0m0 4.162-.176.53a1 1 0 0 1-.633.632l-.529.176.53.176a1 1 0 0 1 .632.633l.176.529.176-.53a1 1 0 0 1 .633-.632l.529-.176-.53-.176a1 1 0 0 1-.632-.633zM12 8.4a.6.6 0 0 1 .57.41l.654 1.966 1.966.655a.6.6 0 0 1 0 1.138l-1.966.655-.655 1.966a.6.6 0 0 1-1.138 0l-.655-1.966-1.966-.655a.6.6 0 0 1 0-1.138l1.966-.655.655-1.966A.6.6 0 0 1 12 8.4"
      />
    </svg>
  );
}

export function useAiSupportPlace() {
  const [place, setPlace] = useState(readAiSupportPlace);
  useEffect(() => {
    const sync = () => setPlace(readAiSupportPlace());
    window.addEventListener(PLACE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PLACE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return place;
}

/** Corner launcher for the docs-chat widget. Side comes from appearance settings. */
export default function AiSupportFab() {
  const place = useAiSupportPlace();
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => subscribeAiSupportChatOpen(setChatOpen), []);

  if (place === 'none') return null;

  return createPortal(
    <>
      <div className={place === 'right' ? 'ai-support-fab is-right' : 'ai-support-fab'}>
        <OverlayTrigger
          placement={place === 'right' ? 'left' : 'right'}
          overlay={
            <Tooltip id="ai-support-fab-tip">پشتیبانی با AI</Tooltip>
          }
        >
          <button
            type="button"
            className={
              chatOpen
                ? 'ai-support-fab__btn is-open'
                : 'ai-support-fab__btn'
            }
            aria-label="پشتیبانی با AI"
            onClick={openAiSupportChat}
          >
            <AiSupportIcon size={26} />
          </button>
        </OverlayTrigger>
      </div>
      <style>
        {`
        .ai-support-fab{
          position:fixed;
          z-index:1035;
          left:1.25rem;
          right:auto;
          bottom:1.25rem;
          width:52px;
          height:52px;
        }
        .ai-support-fab.is-right{
          left:auto;
          right:1.25rem;
        }
        .ai-support-fab__btn{
          width:52px;
          height:52px;
          border:0;
          border-radius:50%;
          padding:0;
          display:flex;
          align-items:center;
          justify-content:center;
          color:#fff;
          background:var(--falcon-primary,#2c7be5);
          box-shadow:0 .35rem .9rem rgba(0,0,0,.18);
          cursor:pointer;
        }
        .ai-support-fab__btn:hover,
        .ai-support-fab__btn.is-open{
          filter:brightness(.94);
        }
        [data-theme-variant='primary'] .ai-support-fab__btn,
        [data-theme-variant='yellow'] .ai-support-fab__btn{
          color:#2d3748;
        }
        `}
      </style>
    </>,
    document.body
  );
}
